import { mediaUrl, pieceName } from "../media";
import Photo from "./Photo";
import { DownloadIcon, Share2Icon, ThumbsDownIcon, ThumbsUpIcon, Trash2Icon } from "lucide-react";
import { ThumbsDownFilled, ThumbsUpFilled } from "../../../components/icons";

function Icon({ label, onClick, icon: Glyph }) {
    return (
        <button type="button" aria-label={label} onClick={onClick} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]">
            <Glyph className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
    );
}

function ResultTools({ result, onDownload, onShare, onLike, onDislike, onDelete, shared, className }) {
    return (
        <div className={className}>
            <div className="flex gap-2">
                <Icon label="Download" onClick={onDownload} icon={DownloadIcon} />
                <Icon label="Share" onClick={onShare} icon={Share2Icon} />
            </div>
            {shared ? null : (
                <div className="flex gap-2">
                    <Icon label="Like" onClick={onLike} icon={result.user_feedback === "positive" ? ThumbsUpFilled : ThumbsUpIcon} />
                    <Icon label="Dislike" onClick={onDislike} icon={result.user_feedback === "negative" ? ThumbsDownFilled : ThumbsDownIcon} />
                    <Icon label="Delete" onClick={onDelete} icon={Trash2Icon} />
                </div>
            )}
        </div>
    );
}

function Portrait({ image, label, onClick, isPhone }) {
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`relative overflow-hidden rounded-lg border-2 border-primary-500 bg-placeholder ${isPhone ? "aspect-[3/4] h-auto w-full" : "h-[min(100cqh,calc(100cqw*4/3))] w-[min(100cqw,calc(100cqh*3/4))] max-h-full max-w-full"}`}
        >
            {image?.image_url ? <Photo src={mediaUrl(image.image_url)} eager className="h-full w-full" /> : null}
            {label ? (
                <span className="absolute bottom-3 left-3 rounded-full bg-white px-2.5 py-1 font-Mulish text-xs font-semibold text-secondary shadow-[0_0_0_1px_var(--hairline)]">
                    {label}
                </span>
            ) : null}
        </Tag>
    );
}

function PieceCard({ image, compact, onRemove }) {
    const name = pieceName(image);
    if (!compact) {
        return (
            <div className="relative min-w-0">
                <Photo src={mediaUrl(image.image_url)} className="aspect-[3/4] w-full rounded-lg shadow-[0_0_0_1px_var(--hairline)]" />
                <button type="button" aria-label={`Remove ${name}`} onClick={onRemove} className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white font-Mulish text-sm text-primary-700 shadow-[0_0_0_1px_var(--hairline)]">
                    ×
                </button>
                <span className="mt-1.5 block truncate px-0.5 font-Mulish text-xs font-semibold text-secondary">{name}</span>
            </div>
        );
    }
    return (
        <div className="relative flex w-[5.75rem] shrink-0 flex-col rounded-lg bg-white p-1.5 shadow-[0_0_0_1px_var(--hairline)]">
            <Photo src={mediaUrl(image.image_url)} className="aspect-[3/4] w-full rounded-md" />
            <span className="truncate px-1 py-1 font-Mulish text-xs font-semibold text-secondary">{name}</span>
            <button type="button" aria-label={`Remove ${name}`} onClick={onRemove} className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white font-Mulish text-xs text-primary-700 shadow-[0_0_0_1px_var(--hairline)]">
                ×
            </button>
        </div>
    );
}

export default function Stage({
    isPhone,
    person,
    pieces,
    result,
    generating,
    earlier,
    onOpenPhoto,
    onOpenPieces,
    onRemovePiece,
    onDownload,
    onShare,
    onLike,
    onDislike,
    onDelete,
    canAddPiece = true,
}) {
    const showingResult = Boolean(result);
    const countLabel = pieces.length === 0
        ? "Add at least one piece"
        : `${pieces.length} ${pieces.length === 1 ? "piece" : "pieces"} in this try-on`;

    return (
        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)]">
            {isPhone ? (
                <div className="mb-3 flex items-center justify-between gap-3">
                    <h2 className="font-Mulish text-lg font-semibold text-secondary">Your look</h2>
                    {earlier}
                </div>
            ) : null}
            <div className={`flex min-h-0 flex-1 flex-col gap-3 rounded-2xl bg-primary-50 p-3 ${isPhone ? "overflow-y-auto" : "overflow-hidden md:flex-row"}`}>
                {showingResult ? (
                    <ResultView
                        isPhone={isPhone}
                        result={result}
                        generating={generating}
                        onDownload={onDownload}
                        onShare={onShare}
                        onLike={onLike}
                        onDislike={onDislike}
                        onDelete={onDelete}
                    />
                ) : person || pieces.length > 0 ? (
                    <>
                        <div className={`flex min-h-0 min-w-0 items-center justify-center overflow-hidden ${isPhone ? "shrink-0" : "flex-1 [container-type:size]"}`}>
                            {person ? (
                                <Portrait image={person} label="Photo" onClick={onOpenPhoto} isPhone={isPhone} />
                            ) : (
                                <button type="button" onClick={onOpenPhoto} className={`flex aspect-[3/4] max-w-full items-center justify-center rounded-lg border border-dashed border-primary-200 bg-white px-6 font-Mulish text-sm font-semibold text-primary-700 ${isPhone ? "h-auto w-full" : "h-full max-h-full w-auto"}`}>
                                    Add a photo
                                </button>
                            )}
                        </div>
                        <div className={`flex min-h-0 flex-col gap-3 ${isPhone ? "shrink-0" : "w-72 shrink-0"}`}>
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="font-Mulish text-sm font-semibold text-secondary">Pieces</span>
                                <span className="min-w-0 truncate text-right font-Mulish text-xs text-graphite">{countLabel}</span>
                            </div>
                            <div className={isPhone ? "flex gap-2 overflow-x-auto" : "grid min-h-0 flex-1 grid-cols-2 content-start gap-x-3 gap-y-4 overflow-y-auto"}>
                                {pieces.map((piece) => (
                                    <PieceCard key={piece.id} image={piece} compact={isPhone} onRemove={() => onRemovePiece(piece.id)} />
                                ))}
                                {canAddPiece ? (
                                    <button type="button" onClick={onOpenPieces} className={`flex shrink-0 items-center justify-center rounded-lg border border-dashed border-primary-200 bg-white px-2 text-center font-Mulish text-xs font-semibold text-primary-700 ${isPhone ? "aspect-[3/4] w-[5.75rem]" : "aspect-[3/4]"}`}>
                                        Add piece
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex min-h-0 flex-1 items-center justify-center">
                        <div className={`flex aspect-[3/4] max-w-full flex-col items-center justify-center px-6 text-center ${isPhone ? "h-auto w-full" : "h-full max-h-full w-auto"}`}>
                            <svg className="mb-2 h-8 w-8 text-primary-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                                <rect x="4" y="5" width="16" height="14" rx="2" />
                                <path d="M4 16l4.5-4.5 3 3L15 11l5 5" />
                            </svg>
                            <p className="font-Mulish text-lg font-semibold text-secondary">No image yet</p>
                            <p className="mt-1 max-w-[220px] font-Mulish text-sm text-graphite">Your try-on will show up here.</p>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

function ResultView({ isPhone, result, generating, onDownload, onShare, onLike, onDislike, onDelete }) {
    const ready = result.status === "completed" && result?.result?.image_url;
    const failed = result.status === "failed";
    const sources = [result.model, ...(result.lookPieces || (result.garment ? [result.garment] : []))].filter((image) => image?.image_url);

    return (
        <div className={`flex min-w-0 flex-col gap-3 ${isPhone ? "shrink-0" : "min-h-0 flex-1"}`}>
            <div className={isPhone ? "shrink-0" : "flex min-h-0 flex-1 justify-center"}>
            <div className={`relative overflow-hidden rounded-lg bg-placeholder shadow-[0_0_0_1px_var(--hairline)] ${isPhone ? "aspect-[3/4] w-full" : "aspect-[3/4] h-full max-h-full w-auto max-w-full"}`}>
                {ready ? (
                    <Photo src={mediaUrl(result.result.image_url)} alt="Try-on" eager fill />
                ) : (
                    <div className="flex h-full w-full items-center justify-center px-6 text-center">
                        <p className="font-Mulish text-sm text-graphite">
                            {failed ? "This try-on did not finish." : generating || result.status === "running" || result.status === "pending" ? "Creating your try-on…" : "Your try-on will show up here."}
                        </p>
                    </div>
                )}
                {ready ? (
                    <ResultTools
                        result={result}
                        onDownload={onDownload}
                        onShare={onShare}
                        onLike={onLike}
                        onDislike={onDislike}
                        onDelete={onDelete}
                        shared={result.access === "public"}
                        className="absolute left-2 right-2 top-2 flex justify-between"
                    />
                ) : null}
            </div>
            </div>
            {sources.length > 0 ? (
                <div className="flex flex-col items-center gap-2">
                    <span className="font-Mulish text-sm font-semibold text-secondary">In this try-on</span>
                    <div className="flex max-w-full justify-center gap-3 overflow-x-auto">
                        {sources.map((image, index) => {
                            const isPerson = Boolean(result.model) && image.id === result.model.id && index === 0;
                            const brand = pieceName(image);
                            const label = isPerson || brand === "Garment" ? null : brand;
                            return (
                                <div key={`${image.id || index}`} className="w-20 shrink-0">
                                    <Photo src={mediaUrl(image.image_url)} className="aspect-[3/4] w-full rounded-lg shadow-[0_0_0_1px_var(--hairline)]" />
                                    {label ? <span className="mt-1.5 block truncate text-center font-Mulish text-xs font-semibold text-secondary">{label}</span> : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
